import React, { useState, useEffect, useCallback } from 'react';
import { getFasesDeObra, deleteFaseDeObra, addFaseDeObra, reactivateFaseDeObra, swapFasesOrdem } from '../services/api';
import FaseObraEditModal from '../components/FaseObraEditModal';
import {
    Container,
    Typography,
    CircularProgress,
    Box,
    IconButton,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Tooltip,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FaseObra } from '../services/types';

const FasesObraPage: React.FC = () => {
    const [fases, setFases] = useState<FaseObra[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [faseToEdit, setFaseToEdit] = useState<FaseObra | null>(null);

    // Add Modal State
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [newNome, setNewNome] = useState('');
    const [newTipoFluxo, setNewTipoFluxo] = useState<'construcao' | 'leilao'>('construcao');
    const [newOrdem, setNewOrdem] = useState<number>(1);
    const [showInactive, setShowInactive] = useState(false);

    const fetchFases = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getFasesDeObra(showInactive);
            setFases(data || []);
        } catch (err) {
            console.error('Erro ao buscar fases:', err);
            setError('Erro ao buscar dados das fases da obra.');
        } finally {
            setLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        fetchFases();
    }, [fetchFases]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Deseja desativar esta fase da obra? Ela poderá ser reativada depois.')) {
            try {
                await deleteFaseDeObra(id);
                fetchFases();
            } catch (err: any) {
                setError(err.message || 'Erro ao desativar fase da obra.');
            }
        }
    };

    const handleReactivate = async (id: number) => {
        try {
            await reactivateFaseDeObra(id);
            fetchFases();
        } catch (err: any) {
            setError(err.message || 'Erro ao reativar fase da obra.');
        }
    };

    const handleEditClick = (fase: FaseObra) => {
        setFaseToEdit(fase);
        setEditModalOpen(true);
    };

    const handleMoveOrder = async (groupFases: FaseObra[], index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= groupFases.length) return;

        const current = groupFases[index];
        const neighbor = groupFases[targetIndex];
        try {
            await swapFasesOrdem(
                { id: current.id, ordem: current.ordem },
                { id: neighbor.id, ordem: neighbor.ordem }
            );
            fetchFases();
        } catch (err: any) {
            setError(err.message || 'Erro ao reordenar fases.');
        }
    };

    const handleAddClick = (defaultFluxo: 'construcao' | 'leilao' = 'construcao') => {
        setNewNome('');
        setNewTipoFluxo(defaultFluxo);
        const maxOrdem = fases.filter(f => f.tipo_fluxo === defaultFluxo).reduce((max, f) => Math.max(max, f.ordem || 0), 0);
        setNewOrdem(maxOrdem + 1);
        setAddModalOpen(true);
    };

    const handleSaveNewFase = async () => {
        if (!newNome.trim()) {
            alert('O nome da fase é obrigatório.');
            return;
        }

        try {
            await addFaseDeObra({ nome: newNome.trim(), tipo_fluxo: newTipoFluxo, ordem: newOrdem } as any);
            setAddModalOpen(false);
            fetchFases();
        } catch (err: any) {
            console.error('Erro ao adicionar fase:', err);
            alert(err.message || 'Erro ao adicionar fase da obra.');
        }
    };

    const renderFluxoTable = (titulo: string, tipoFluxo: 'construcao' | 'leilao', corChip: 'primary' | 'warning') => {
        const fasesGrupo = fases.filter(f => f.tipo_fluxo === tipoFluxo);

        return (
            <Paper elevation={2} sx={{ mb: 4, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h6" fontWeight="bold">{titulo}</Typography>
                        <Chip label={`${fasesGrupo.length} fases`} size="small" color={corChip} variant="outlined" />
                    </Box>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddClick(tipoFluxo)}>
                        Adicionar em {tipoFluxo === 'leilao' ? 'Leilão' : 'Construção'}
                    </Button>
                </Box>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width={80}><strong>Ordem</strong></TableCell>
                                <TableCell><strong>Nome da Fase</strong></TableCell>
                                <TableCell width={140} align="center"><strong>Reordenar</strong></TableCell>
                                <TableCell width={120} align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fasesGrupo.map((fase, idx) => (
                                <TableRow
                                    key={fase.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'action.hover' }, opacity: fase.ativo ? 1 : 0.5 }}
                                >
                                    <TableCell component="th" scope="row">
                                        <Chip label={`#${fase.ordem}`} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        {fase.nome}
                                        {!fase.ativo && <Chip label="Inativo" size="small" color="default" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Subir ordem">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === 0}
                                                    onClick={() => handleMoveOrder(fasesGrupo, idx, 'up')}
                                                >
                                                    <ArrowUpwardIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Descer ordem">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === fasesGrupo.length - 1}
                                                    onClick={() => handleMoveOrder(fasesGrupo, idx, 'down')}
                                                >
                                                    <ArrowDownwardIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        {fase.ativo ? (
                                            <>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" onClick={() => handleEditClick(fase)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Desativar">
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(fase.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <Tooltip title="Reativar">
                                                <IconButton size="small" color="success" onClick={() => handleReactivate(fase.id)}>
                                                    <RestoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {fasesGrupo.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        Nenhuma fase cadastrada para este fluxo.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        );
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Fases da Obra
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant={showInactive ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setShowInactive(!showInactive)}
                        color="inherit"
                    >
                        {showInactive ? 'Ocultar Inativos' : 'Mostrar Inativos'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddClick('construcao')}
                        sx={{ borderRadius: 2 }}
                    >
                        Nova Fase
                    </Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {renderFluxoTable('🏗️ Fluxo de Construção', 'construcao', 'primary')}
                    {renderFluxoTable('🏠 Fluxo de Leilão', 'leilao', 'warning')}
                </>
            )}

            {/* Add Modal */}
            <Dialog open={isAddModalOpen} onClose={() => setAddModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Adicionar Nova Fase</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            select
                            label="Tipo de Fluxo"
                            fullWidth
                            value={newTipoFluxo}
                            onChange={(e) => {
                                const tipo = e.target.value as 'construcao' | 'leilao';
                                setNewTipoFluxo(tipo);
                                const maxOrdem = fases.filter(f => f.tipo_fluxo === tipo).reduce((max, f) => Math.max(max, f.ordem || 0), 0);
                                setNewOrdem(maxOrdem + 1);
                            }}
                        >
                            <MenuItem value="construcao">Construção</MenuItem>
                            <MenuItem value="leilao">Leilão</MenuItem>
                        </TextField>
                        <TextField
                            autoFocus
                            label="Nome da Fase"
                            fullWidth
                            value={newNome}
                            onChange={(e) => setNewNome(e.target.value)}
                            required
                        />
                        <TextField
                            label="Ordem"
                            type="number"
                            fullWidth
                            value={newOrdem}
                            onChange={(e) => setNewOrdem(Number(e.target.value))}
                            required
                            helperText="Número sequencial da fase dentro do fluxo"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSaveNewFase} variant="contained">Adicionar</Button>
                </DialogActions>
            </Dialog>

            <FaseObraEditModal
                fase={faseToEdit}
                open={isEditModalOpen}
                onClose={() => setEditModalOpen(false)}
                onFaseObraUpdated={fetchFases}
            />
        </Container>
    );
};

export default FasesObraPage;
